import React, { useEffect, useRef, useState } from 'react';
import jm, { formatDateTime, photoUrl, referredBy, toInputDate } from '../api';
import CandidateTable, { NameCell, RowActions, StatusBadge } from '../common/CandidateTable';
import Modal from '../common/Modal';
import RatingRow from '../common/RatingRow';
import CandidateSummary, {
  SectionHeader,
} from '../common/CandidateSummary';
import useStage from '../common/useStage';
import { useMasters } from '../common/MastersContext';
import SearchSelect, { departmentOptions, jobOptions } from '../common/SearchSelect';
import DateField from '../common/DateField';
import NumberField from '../common/NumberField';

/** How the phone round ended — drives the badge in the list. */
const INTERVIEW_STATUS = [
  { value: 2, label: 'Not Completed', tone: 'primary' },
  { value: 1, label: 'Completed', tone: 'success' },
  { value: 3, label: 'No Response', tone: 'warning' },
  { value: 4, label: 'Not Interest', tone: 'danger' },
];

const NEXT_STATUS = [
  { value: 'Shortlisted', label: 'Phone (stay here)' },
  { value: 'Demo', label: 'Demo' },
  { value: 'hold', label: 'Hold' },
  { value: 'Rejected', label: 'Rejected' },
];

const RATINGS = [
  { name: 'communication', label: 'Communication' },
  { name: 'Reason', label: 'Reason For Interest' },
  { name: 'Stabilily', label: 'Job Stability' },
  { name: 'Education', label: 'Education / Training' },
  { name: 'Accomplishment', label: 'Accomplishment' },
];

const PhoneInterview = ({ onMoved }) => {
  const masters = useMasters();
  const [form, setForm] = useState({});
  const resumeRef = useRef(null);

  const { rows, loading, editing, detail, detailLoading, openEdit, closeEdit, saving, save } =
    useStage(jm.shortlisted, jm.saveShortlisted, { onMoved });

  const set = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  useEffect(() => {
    if (!detail) return;
    const app = detail.application;
    const stage = detail.shortlisted || {};

    setForm({
      first_name: app.first_name || '',
      last_name: app.last_name || '',
      email: app.email || '',
      dob: toInputDate(app.dob),
      contact_no: app.contact_no || '',
      job_id: app.job_id || '',
      department_id: app.department_id || '',
      communication: Number(stage.communication) || 0,
      Reason: Number(stage.Reason) || 0,
      Stabilily: Number(stage.Stabilily) || 0,
      Education: Number(stage.Education) || 0,
      Accomplishment: Number(stage.Accomplishment) || 0,
      c_salary: stage.c_salary || app.current_salary || '',
      e_salary: stage.e_salary || app.expected_salary || '',
      interview_status: Number(stage.interview_status) || 0,
      shortlist_remark: stage.notes || '',
      job_status: 'Shortlisted',
      demo_notes: '',
      hold_notes: '',
      reject_notes: '',
    });
  }, [detail]);

  const liveScore = RATINGS.reduce((sum, field) => sum + (Number(form[field.name]) || 0), 0);

  const submit = () => save({ ...form, resume_file: resumeRef.current?.files?.[0] });

  const columns = [
    { key: 'action', label: 'Action', search: false, sortable: false, render: (row) => <RowActions row={row} onEdit={openEdit} /> },
    { key: 'job_title', label: 'Job Title' },
    { key: 'first_name', label: 'Candidate Name', render: (row) => <NameCell row={row} /> },
    { key: 'Referred_others', label: 'Referred By', render: referredBy, sortValue: referredBy },
    { key: 'email', label: 'Email' },
    {
      key: 'interview_status',
      label: 'Status',
      search: false,
      render: (row) => {
        const status = INTERVIEW_STATUS.find((s) => s.value === Number(row.interview_status));
        return (
          <StatusBadge
            tone={status?.tone}
            label={status ? `Phone Interview — ${status.label}` : 'Phone Interview'}
          />
        );
      },
    },
    { key: 'done_on', label: 'Shortlisted On', render: (row) => formatDateTime(row.done_on) },
  ];

  return (
    <>
      <CandidateTable
        title="Phone Interview"
        rows={rows}
        columns={columns}
        loading={loading}
        exportName="phone_interview"
      />

      <Modal
        show={!!editing}
        title={editing ? `${editing.first_name} ${editing.last_name || ''}` : ''}
        subtitle={editing ? editing.job_title : ''}
        photo={photoUrl(editing?.emp_img)}
        onClose={closeEdit}
        onSubmit={submit}
        saving={saving}
      >
        {detailLoading && <div className="text-center py-4">Loading…</div>}

        {!detailLoading && detail && (
          <>
            <CandidateSummary candidate={detail.application} />

            <div className="border rounded p-3 mb-3">
              <SectionHeader title="Phone Interview" score={liveScore} max={25} />

              {RATINGS.map((field) => (
                <RatingRow
                  key={field.name}
                  label={field.label}
                  name={field.name}
                  value={form[field.name]}
                  onChange={set}
                />
              ))}

              <div className="row mt-2">
                <div className="col-md-6 form-group">
                  <label>Current Salary</label>
                  <NumberField value={form.c_salary} onChange={(v) => set('c_salary', v)} />
                </div>
                <div className="col-md-6 form-group">
                  <label>Expected Salary</label>
                  <NumberField value={form.e_salary} onChange={(v) => set('e_salary', v)} />
                </div>
              </div>
            </div>

            <div className="row">
              <div className="col-md-6 form-group required-tooltip">
                <label>Job Status</label>
                <select className="form-control required-input" value={form.job_status || ''} onChange={(e) => set('job_status', e.target.value)}>
                  {NEXT_STATUS.map((status) => (
                    <option key={status.value} value={status.value}>{status.label}</option>
                  ))}
                </select>
              </div>

              {form.job_status === 'Shortlisted' && (
                <div className="col-md-6 form-group required-tooltip">
                  <label>Interview Status</label>
                  <select className="form-control required-input" value={form.interview_status || 0} onChange={(e) => set('interview_status', Number(e.target.value))}>
                    <option value={0}>Choose</option>
                    {INTERVIEW_STATUS.map((status) => (
                      <option key={status.value} value={status.value}>{status.label}</option>
                    ))}
                  </select>
                </div>
              )}

              <div className="col-md-12 form-group">
                <label>Remark</label>
                <textarea className="form-control" rows="2" value={form.shortlist_remark || ''} onChange={(e) => set('shortlist_remark', e.target.value)} />
              </div>
            </div>

            {form.job_status === 'Demo' && (
              <div className="form-group">
                <label>Demo Notes</label>
                <textarea className="form-control" rows="2" value={form.demo_notes || ''} onChange={(e) => set('demo_notes', e.target.value)} />
              </div>
            )}

            {form.job_status === 'hold' && (
              <div className="form-group">
                <label>Hold Notes</label>
                <textarea className="form-control" rows="2" value={form.hold_notes || ''} onChange={(e) => set('hold_notes', e.target.value)} />
              </div>
            )}

            {form.job_status === 'Rejected' && (
              <div className="form-group">
                <label>Reject Notes</label>
                <textarea className="form-control" rows="2" value={form.reject_notes || ''} onChange={(e) => set('reject_notes', e.target.value)} />
              </div>
            )}

            <h6 className="mt-4 mb-3">Edit Candidate</h6>
            <div className="row">
              <div className="col-md-6 form-group">
                <label>First Name</label>
                <input className="form-control" value={form.first_name || ''} onChange={(e) => set('first_name', e.target.value)} />
              </div>
              <div className="col-md-6 form-group">
                <label>Last Name</label>
                <input className="form-control" value={form.last_name || ''} onChange={(e) => set('last_name', e.target.value)} />
              </div>
              <div className="col-md-6 form-group">
                <label>Date of Birth</label>
                <DateField value={form.dob || ''} onChange={(v) => set('dob', v)} />
              </div>
              <div className="col-md-6 form-group">
                <label>Contact Number</label>
                <input className="form-control" maxLength={10} value={form.contact_no || ''} onChange={(e) => set('contact_no', e.target.value)} />
              </div>
              <div className="col-md-6 form-group">
                <label>Email</label>
                <input className="form-control" value={form.email || ''} onChange={(e) => set('email', e.target.value)} />
              </div>
              <div className="col-md-6 form-group">
                <label>Resume</label>
                <input type="file" className="form-control-file" ref={resumeRef} accept=".pdf,.doc,.docx" />
                <small className="text-muted">Leave empty to keep the current resume</small>
              </div>
              <div className="col-md-6 form-group">
                <label>Department</label>
                <SearchSelect
                  value={form.department_id}
                  onChange={(v) => set('department_id', v)}
                  options={departmentOptions(masters.departments)}
                  placeholder="Search department…"
                />
              </div>
              <div className="col-md-6 form-group">
                <label>Job Title</label>
                <SearchSelect
                  value={form.job_id}
                  onChange={(v) => set('job_id', v)}
                  options={jobOptions(masters.jobs)}
                  placeholder="Search job title…"
                />
              </div>
            </div>
          </>
        )}
      </Modal>
    </>
  );
};

export default PhoneInterview;
